/**
 * TS-P4-GPS-AUTOCLOCKIN — geofence distance + one-shot position helpers.
 * Never throws to the caller in a way that blocks a punch: all failures
 * resolve to `null`, letting callers no-op silently (PIPEDA: opt-in, never
 * blocks).
 */
import { Capacitor } from '@capacitor/core'
import { Geolocation } from '@capacitor/geolocation'

/** Great-circle distance in meters between two lat/lng points. */
export function haversineMeters(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const earthRadiusM = 6371000
  const toRad = (deg: number) => (deg * Math.PI) / 180
  const dLat = toRad(lat2 - lat1)
  const dLng = toRad(lng2 - lng1)
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) * Math.sin(dLng / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  return earthRadiusM * c
}

/**
 * One-shot best-effort position read for stamping a punch's coordinates.
 * Short timeout, no fallback to a stale cached position (a clock-in/out
 * coordinate should reflect "now", not wherever the device last was).
 * Resolves `null` on denial, timeout, or an unsupported browser — callers
 * must treat that as "no location available", not an error.
 */
export function getCurrentPositionOnce(timeoutMs = 4000): Promise<{ lat: number; lng: number } | null> {
  let positionPromise: Promise<{ lat: number; lng: number }>

  // Native builds must use the Capacitor plugin, not navigator.geolocation —
  // the web API's permission prompt shows the WKWebView origin ("localhost")
  // instead of the app name (Joe report, build 80).
  if (Capacitor.isNativePlatform()) {
    positionPromise = Geolocation.getCurrentPosition({ enableHighAccuracy: true, timeout: timeoutMs })
      .then(pos => ({ lat: pos.coords.latitude, lng: pos.coords.longitude }))
  } else {
    if (!('geolocation' in navigator)) {
      return Promise.resolve(null)
    }

    positionPromise = new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        pos => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        reject,
        { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 0 }
      )
    })
  }

  // Some native and browser implementations have been observed to ignore
  // their own timeout option. Own the deadline here so a punch can always
  // continue without coordinates, and clear it when location settles first.
  return new Promise(resolve => {
    const deadline = setTimeout(() => resolve(null), timeoutMs)
    positionPromise.then(
      position => {
        clearTimeout(deadline)
        resolve(position)
      },
      () => {
        clearTimeout(deadline)
        resolve(null)
      }
    )
  })
}

export interface PunchLocation {
  lat: number
  lng: number
}

const PUNCH_LOCATION_DEADLINE_MS = 3000

/** Run a punch after a separately bounded, best-effort location attempt. */
export async function punchWithBestEffortLocation<T>(
  punch: (position: PunchLocation | null) => Promise<T>,
): Promise<T> {
  const location = Promise.resolve().then(() => getCurrentPositionOnce())
  let deadline: ReturnType<typeof setTimeout> | undefined
  const position = await Promise.race<PunchLocation | null>([
    location.catch(() => null),
    new Promise<null>(resolve => {
      deadline = setTimeout(() => resolve(null), PUNCH_LOCATION_DEADLINE_MS)
    }),
  ])
  if (deadline !== undefined) clearTimeout(deadline)
  return punch(position)
}

export interface GeoWatchOptions {
  enableHighAccuracy?: boolean
  timeout?: number
  maximumAge?: number
}

/**
 * Watches position, calling `onPosition` on every update and `onError` on
 * denial/failure/unsupported. Returns an unsubscribe function safe to use
 * as a React effect's cleanup. Native builds route through the Capacitor
 * plugin for the same reason as `getCurrentPositionOnce` — the web API's
 * permission prompt shows the WKWebView origin, not the app name.
 */
export function watchPosition(
  onPosition: (lat: number, lng: number) => void,
  onError: () => void,
  options: GeoWatchOptions = {}
): () => void {
  const opts = { enableHighAccuracy: true, timeout: 10000, ...options }

  if (Capacitor.isNativePlatform()) {
    let cancelled = false
    let watchId: string | null = null
    Geolocation.watchPosition(opts, (pos, err) => {
      if (cancelled) return
      if (err || !pos) { onError(); return }
      onPosition(pos.coords.latitude, pos.coords.longitude)
    }).then(id => {
      if (cancelled) Geolocation.clearWatch({ id })
      else watchId = id
    }).catch(() => { if (!cancelled) onError() })
    return () => {
      cancelled = true
      if (watchId) Geolocation.clearWatch({ id: watchId })
    }
  }

  if (!('geolocation' in navigator)) {
    onError()
    return () => {}
  }
  const id = navigator.geolocation.watchPosition(
    pos => onPosition(pos.coords.latitude, pos.coords.longitude),
    onError,
    opts
  )
  return () => navigator.geolocation.clearWatch(id)
}
